import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  PRESET_MOBILE_TABS,
  PRESET_PANELS,
  WORKSPACE_STORAGE_KEY,
  normalizeWorkspacePreset,
  type WorkspaceMobileTab,
  type WorkspacePanelId,
  type WorkspacePreset,
} from './workspacePresets'

type StoredPreferences = {
  preset: WorkspacePreset
}

function readStored(): StoredPreferences {
  try {
    const raw = localStorage.getItem(WORKSPACE_STORAGE_KEY)
    if (!raw) return { preset: 'simple' }
    const parsed = JSON.parse(raw) as Partial<StoredPreferences>
    return { preset: normalizeWorkspacePreset(parsed.preset) }
  } catch {
    /* ignore */
  }
  return { preset: 'simple' }
}

export type WorkspacePreferencesValue = {
  preset: WorkspacePreset
  setPreset: (next: WorkspacePreset) => void
  visiblePanels: WorkspacePanelId[]
  mobileTabs: WorkspaceMobileTab[]
  isPanelVisible: (panel: WorkspacePanelId) => boolean
}

const WorkspacePreferencesContext = createContext<WorkspacePreferencesValue | null>(null)

function useWorkspacePreferencesState(): WorkspacePreferencesValue {
  const [preset, setPresetState] = useState<WorkspacePreset>(() => readStored().preset)

  useEffect(() => {
    localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify({ preset }))
  }, [preset])

  const setPreset = useCallback((next: WorkspacePreset) => {
    setPresetState(next)
  }, [])

  const visiblePanels = useMemo(() => PRESET_PANELS[preset], [preset])
  const mobileTabs = useMemo(() => PRESET_MOBILE_TABS[preset], [preset])

  const isPanelVisible = useCallback(
    (panel: WorkspacePanelId) => visiblePanels.includes(panel),
    [visiblePanels]
  )

  return {
    preset,
    setPreset,
    visiblePanels,
    mobileTabs,
    isPanelVisible,
  }
}

export function WorkspacePreferencesProvider({ children }: { children: React.ReactNode }) {
  const value = useWorkspacePreferencesState()
  return React.createElement(WorkspacePreferencesContext.Provider, { value }, children)
}

export function useWorkspacePreferences(): WorkspacePreferencesValue {
  const ctx = useContext(WorkspacePreferencesContext)
  if (!ctx) {
    throw new Error('useWorkspacePreferences must be used within WorkspacePreferencesProvider')
  }
  return ctx
}

export type WorkspacePreferences = WorkspacePreferencesValue
